import {
  ColumnHeader,
  ColumnHeaderDisplayName,
  ProductsCompareExport,
  ProductsParam,
  RetailerKpiOption,
  SaleType,
  TrendsKpi,
} from '@models/Products';
import { PagesConfig } from '@utils/pageConfig/PagesConfigContext';
import { FactRevenue, Option, PageName, PageType } from '@utils/pageConfig/Types';
import { Periodicity } from '@components/time-period-selector/PeriodsTypes';
import { ExportFormat } from '@ecosystem/newron-client-lib';
import { getAppliedFiltersText } from '@hooks/export/ExportUtils';
import { ExportLineChartOptions, useExportMultiChart } from '@hooks/export/utilities/UseExportMultiChart';
import { RI_PRODUCT_TREND_TOP_5_PRODUCTS_CARD_TITLE } from '@pages/Utilities';
import { PRODUCTS_TRENDS_TOP_5_PRODUCTS } from '@utils/StringConstants';
import {
  dataExportedTrackingFunction,
  DataExportTrackingElementParam,
  useDataExportTracking,
} from '@utils/page-analytics/model/DataExportTracking';
import { ElementType } from '@utils/page-analytics/model/AnalyticTrackingBasePropertiesObj';
import { getExportTrackingFormat } from '@hooks/export/export-tracking/ExportTracking';
import { formatProductsPeriod } from '@utils/PeriodUtils';
import getComparisonPeriod, { PeriodFormat } from '@components/filter-selection-note/GetComparisonPeriod';
import { RelativePeriod } from '@components/comparison-period-selector/Types';
import { capitalize } from 'lodash';
import { MARKET, RETAILER } from '@components/products-compare-modal/Types';
import React from 'react';
import { KpiTypesOption } from '@components/products-top-5-table/Types';
import { SaleTypeMappingProducts } from '@const/PerformanceType';
import { BaseTimePeriod } from '@services/FiltersTypes';
import { useExportImages } from '@hooks/export/UseExportChartImage';
import { ChartKeys } from '@const/ChartKeys';
import { generateUniqueChartKeys } from '@data-visualization/utilities/GenerateUniqueChartKeys';
import {
  processProductsMarketLineChartData,
  processProductsRetailerLineChartData,
} from '@data-visualization/utilities/line-chart/FormatLineChartData';

export interface UseTrendsExportParam {
  productsParam?: ProductsParam;
  mainFilterStateProducts: any;
  productsCompareExport: ProductsCompareExport[];
  pagesConfig: PagesConfig;
  appliedCountries: Option[];
  appliedPg?: Option[];
  factRevenue: FactRevenue;
  timeFrame: Periodicity;
  selectedSalesType: SaleType;
  selectedRetailerOption: RetailerKpiOption;
  comparisonPeriod: RelativePeriod | BaseTimePeriod;
  selectedKpi: KpiTypesOption;
  latestPeriod: BaseTimePeriod;
  trendsKpi: TrendsKpi;
}

export const useTrendsExport = (param: UseTrendsExportParam): ((exportFormats: ExportFormat[]) => Promise<void>) => {
  const {
    productsParam,
    productsCompareExport,
    pagesConfig,
    appliedCountries,
    appliedPg,
    factRevenue,
    timeFrame,
    selectedSalesType,
    selectedRetailerOption,
    comparisonPeriod,
    selectedKpi,
    latestPeriod,
    trendsKpi,
  } = param;

  const { exportImages } = useExportImages(PageType.RI_PRODUCT);
  const { download: exportLineChart } = useExportMultiChart();

  const handleDataExportTrackingFunction = useDataExportTracking('mainFiltersProducts', 'hitlistsRetailerClient');

  const data: ProductsCompareExport[] = productsCompareExport;
  const handleExportedTracking = (exportFormats: ExportFormat[]) => {
    const dataExportedTrackingParam: DataExportTrackingElementParam = {
      elementType: ElementType.LINE_CHART,
      elementKpi: ColumnHeaderDisplayName[pagesConfig.sortingOptions.headerKey as ColumnHeader],
      elementSort: pagesConfig.sortingOptions,
      optionChosen: exportFormats.map((format) => getExportTrackingFormat(format)),
    };
    handleDataExportTrackingFunction(dataExportedTrackingParam, dataExportedTrackingFunction);
  };
  return React.useCallback(
    async (exportFormats: ExportFormat[]): Promise<void> => {
      if (!productsParam || productsCompareExport.length < 1) {
        return;
      }

      const comparisonTimePeriod = getComparisonPeriod(
        latestPeriod,
        productsParam.periodType,
        comparisonPeriod,
        PeriodFormat.LONG,
      );
      const kpiTitle = capitalize(SaleTypeMappingProducts[selectedSalesType]);
      const marketTitle = MARKET.concat(kpiTitle);

      const retailerTitle =
        selectedSalesType === SaleType.price
          ? `${RETAILER}${kpiTitle}`
          : `${RETAILER}${kpiTitle} ${capitalize(selectedRetailerOption)}`;

      const parseSelectedKpi = {
        label: selectedKpi?.label ? selectedKpi?.label : '',
        value: selectedKpi?.value,
      };

      const exportTitle = `${RI_PRODUCT_TREND_TOP_5_PRODUCTS_CARD_TITLE}`;
      const marketChartData = processProductsMarketLineChartData(trendsKpi, selectedSalesType);
      const retailerChartData = processProductsRetailerLineChartData(
        trendsKpi,
        selectedSalesType,
        selectedRetailerOption,
      );

      const exportImage = [
        exportImages[generateUniqueChartKeys(marketChartData, ChartKeys[PageType.RI_PRODUCT].MARKET)],
        exportImages[generateUniqueChartKeys(retailerChartData, ChartKeys[PageType.RI_PRODUCT].RETAILER)],
      ];

      const options: ExportLineChartOptions = {
        exportFormats,
        title: exportTitle,
        appliedCountry: appliedCountries,
        appliedPg: appliedPg,
        data,
        exportImage,
        timePeriod: formatProductsPeriod(latestPeriod.value, productsParam.periodType, true),
        comparisonTimePeriod: formatProductsPeriod(comparisonTimePeriod, productsParam.periodType, true),
        factRevenue: factRevenue,
        periodType: productsParam.periodType,
        showPerformanceWithin: parseSelectedKpi.label,
        appliedFiltersText: getAppliedFiltersText(pagesConfig, PageType.RI_PRODUCT),
        selectedKpi: parseSelectedKpi,
        chartTitles: [PRODUCTS_TRENDS_TOP_5_PRODUCTS, PRODUCTS_TRENDS_TOP_5_PRODUCTS],
        chartSubTitles: [marketTitle, retailerTitle],
        pageName: PageName.RI_PRODUCT_TRENDS,
      };

      await exportLineChart(options);

      handleExportedTracking(exportFormats);
    },
    [productsParam, productsCompareExport, timeFrame, factRevenue, exportImages],
  );
};
